// ============================================================================
// 建物データ取得 / Microsoft Building Footprints 自前ビルドPMTiles
// ----------------------------------------------------------------------------
// OSM(buildingTiles.ts)・国土地理院試験タイル(gsiBuildingTiles.ts)に続く
// 第三の候補。Microsoftが衛星画像から自動抽出した建物フットプリント
// (https://github.com/microsoft/GlobalMLBuildingFootprints)を、OSMと同じ
// 「事前にtippecanoeでPMTiles化して自前ホスト」方式で取得する(配布形式が
// quadkey単位の巨大GeoJSONLファイルのみで、GSIのようなXYZタイルAPIは無い
// ため、ライブ取得は不可)。
//
// 実測(2026-07-28、鹿骨二丁目700m四方 / さいたま市花火大会3km四方)では
// OSM(12件/50,139件)・GSI試験タイル(139件/6,959件)のどちらよりも多い
// 1,011件/69,863件を確認しており、3ソース中もっとも網羅性が高い。
// ただし高さ属性(height)は日本のデータで実測した範囲内では全件-1(未推定)
// だったため、ビルド時に破棄しており、常に既定値9mを使う(OSM/GSIと同じ
// 前提)。ビルド手順は WORKLOG.md 参照。
// ============================================================================

import { VectorTile } from "@mapbox/vector-tile";
import { PbfReader } from "pbf";
import { distanceMeters } from "./geo";
import type { Building, LngLat } from "./types";
import {
  TILE_ZOOM,
  getPMTiles,
  localToLngLat,
  ringArea,
  ringIntersectsBbox,
  tileBboxDistance,
  tilesForBbox,
} from "./pmtilesCommon";

/** Cloudflare R2(パブリックバケット、CORS設定済み)でホスティング。 */
const TILE_URL = "https://pub-aac031ce777c439bbb845a5887dd9b21.r2.dev/japan-ms-buildings.pmtiles";

// buildingTiles.ts/gsiBuildingTiles.tsと同じ安全弁(理由もそちらと同じ)。
const MAX_DECODED_FEATURES = 300000;
const DEFAULT_HEIGHT_M = 9;

/**
 * bbox [south, west, north, east] にかかるz12タイルをすべて取得し、
 * buildingレイヤーをBuilding[]に変換する(高さ属性が無いため既定値9mで代用)。
 */
export async function fetchMsBuildingsFromTiles(
  south: number,
  west: number,
  north: number,
  east: number
): Promise<Building[]> {
  const p = getPMTiles(TILE_URL);
  const center: LngLat = [(west + east) / 2, (south + north) / 2];
  const tileCoords = tilesForBbox(south, west, north, east, TILE_ZOOM).sort(
    (a, b) =>
      tileBboxDistance(center, a.tx, a.ty, TILE_ZOOM) -
      tileBboxDistance(center, b.tx, b.ty, TILE_ZOOM)
  );

  const tileBytes = await Promise.all(tileCoords.map(({ tx, ty }) => p.getZxy(TILE_ZOOM, tx, ty)));

  const candidates: (Building & { dist: number })[] = [];
  let nextId = 1;
  for (let t = 0; t < tileCoords.length; t++) {
    if (candidates.length >= MAX_DECODED_FEATURES) break;
    const res = tileBytes[t];
    if (!res) continue;
    const { tx, ty } = tileCoords[t];
    const tile = new VectorTile(new PbfReader(new Uint8Array(res.data)));
    const layer = tile.layers.buildings;
    if (!layer) continue;
    for (let i = 0; i < layer.length; i++) {
      const f = layer.feature(i);
      const rings = f
        .loadGeometry()
        .map((pts) =>
          pts.map((pt): LngLat => localToLngLat(pt.x, pt.y, tx, ty, TILE_ZOOM, f.extent))
        )
        .filter((ring) => ring.length >= 3);
      if (rings.length === 0) continue;
      const outer = rings.reduce((a, b) => (ringArea(b) > ringArea(a) ? b : a));
      if (!ringIntersectsBbox(outer, south, west, north, east)) continue;
      candidates.push({
        id: nextId++,
        ring: outer,
        height: DEFAULT_HEIGHT_M,
        estimated: true,
        dist: distanceMeters(center, outer[0]),
      });
    }
  }
  candidates.sort((a, b) => a.dist - b.dist);
  return candidates.slice(0, MAX_DECODED_FEATURES).map(({ dist: _dist, ...b }) => b);
}
